import { useCallback, useMemo, useState } from 'react';
import { BettingType } from '../../@types';
import { BETTING_TYPE_HIT_COUNT_MAP } from '../../constants';
import { useBetslipContext } from '../../contexts/BetslipContext';
import { hasUpdateStakesAction } from '../../helpers';
import { useBetslipBonus } from '../useBetslipBonus';
import { useRoles } from '../useRoles';
import {
  StakeAmountInput,
  useBetslipCalculations,
  useBetslipCombinations,
  useBetslipDisabledState,
  useBetslipDrawerState,
  useBetslipEffects,
  useBetslipMenuHandlers,
  useBetslipMenuOptions,
  useBetslipPayload,
  useBetslipStakeAmounts,
  useBetslipStakeErrors,
  useBetslipStakeValidation,
  useBetslipSystemTicket,
  usePendingBetslipTracker,
  usePlaceBet,
} from '../useBetslip';

export const NEOBET_BETSLIP_PRESET_AMOUNTS = [50, 100, 250, 500] as const;

export const useNeobetBetslipDrawer = () => {
  const [disabledSystems, setDisabledSystems] = useState<string[]>([]);
  const { isAgent } = useRoles();

  const {
    open,
    setOpen,
    showSystemBetInputs,
    toggleSystemBetInputs,
    selectedMenuItem,
    setSelectedMenuItem,
    selectedPlayer,
    setSelectedPlayer,
    closeBetslipDrawer,
  } = useBetslipDrawerState();

  const { localizedBankerMenuOptions, localizedWalletTypeOptions } = useBetslipMenuOptions();
  const { stakeErrors, setStakeErrors, removeSingleStakeError, clearStakeErrors } = useBetslipStakeErrors();

  const {
    bets,
    removeBet,
    removeAllBets,
    toggleBanker,
    errors,
    infoMessages,
    updateSinglesStakeAmount,
    reofferedBettingSlipsIds,
    betslipTicketType,
    selectedWallet,
    isWaysTicket,
    currentBankerCount,
    isMaxBankerCountReached,
    hasBetBuilderSelection,
  } = useBetslipContext();

  const discardBetslip = useCallback(() => {
    closeBetslipDrawer();
    removeAllBets();
    setSelectedPlayer(null);
  }, [closeBetslipDrawer, removeAllBets, setSelectedPlayer]);

  const { hasPendingBetslips } = usePendingBetslipTracker({
    onAllBetslipsResolved: discardBetslip,
  });

  const { stakeAmounts, setStakeAmounts, updateStakeAmount, resetSystemStakeAmountValues } = useBetslipStakeAmounts({
    removeSingleStakeError,
  });

  const { placeBetMutation } = usePlaceBet({
    discardBetslip,
    onSuccess: () => setStakeAmounts({} as StakeAmountInput),
  });

  const { betTypes, preparedBets, maxPayout, totalStakeAmount, totalOdds, minWinningAmount, minOdds, maxOdds } =
    useBetslipCalculations(stakeAmounts);
  const { isSystemTicket } = useBetslipSystemTicket(betTypes);
  const { bonusAmount, totalWinningAmount } = useBetslipBonus({
    maxPayout,
    isSystemTicket,
    selectedWallet,
  });

  const { baseDisabled, disabledByRole, showInsufficientFunds, isAuthenticated } = useBetslipDisabledState({
    totalStakeAmount,
    isAgent,
    selectedPlayerId: selectedPlayer?.id,
    hasPendingBetslips,
  });

  const isPlaceBetButtonDisabled = baseDisabled || disabledByRole || placeBetMutation.isPending;
  const hasReofferedBetslips = reofferedBettingSlipsIds.length > 0;
  const drawerOpen = open || bets.length === 0;
  const shouldCalculateCombinations = bets.length > 0;

  const combinations = useBetslipCombinations(
    preparedBets,
    currentBankerCount,
    showSystemBetInputs,
    shouldCalculateCombinations
  );
  const lastCombinationIndex = useMemo(() => Object.entries(combinations).length - 1, [combinations]);
  const [lastCombinationKey, lastCombinationValue] = useMemo(
    () => Object.entries(combinations)[lastCombinationIndex] || [],
    [combinations, lastCombinationIndex]
  );

  const { validateStake, handleStakeChange, updateStakes } = useBetslipStakeValidation({
    totalOdds,
    preparedBets,
    disabledSystems,
    setDisabledSystems,
    stakeErrors,
    setStakeErrors,
    stakeAmounts,
    updateStakeAmount,
    updateSinglesStakeAmount,
    combinations,
    removeSingleStakeError,
  });

  const { handleMenuChange, handleWalletChange } = useBetslipMenuHandlers(setSelectedMenuItem, setStakeAmounts);
  const { preparePayload } = useBetslipPayload({
    betTypes,
    totalStakeAmount,
    isAgent,
    selectedPlayerId: selectedPlayer?.id,
  });

  const placeBet = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const payload = preparePayload();
      placeBetMutation.mutate(payload);
    },
    [placeBetMutation, preparePayload]
  );

  useBetslipEffects(
    selectedMenuItem,
    setSelectedMenuItem,
    closeBetslipDrawer,
    resetSystemStakeAmountValues,
    clearStakeErrors
  );

  const singleBet = bets.length === 1 ? bets[0] : null;
  const fallbackMainTicketKey = useMemo(() => {
    if (singleBet) {
      return undefined;
    }

    const requiredHitCount = Math.max(1, bets.length - currentBankerCount);

    return Object.entries(BETTING_TYPE_HIT_COUNT_MAP).find(([, hitCount]) => hitCount === requiredHitCount)?.[0] as
      | BettingType
      | undefined;
  }, [bets.length, currentBankerCount, singleBet]);

  const primaryStakeMode = singleBet ? 'single' : 'mainTicket';
  const primaryStakeKey =
    singleBet?.outcomeId ?? (lastCombinationKey as BettingType | undefined) ?? fallbackMainTicketKey;
  const primaryStakeValue = singleBet
    ? (singleBet.singlesStakeAmount?.toString() ?? '')
    : primaryStakeKey
      ? (stakeAmounts[primaryStakeKey as BettingType] ?? '')
      : '';

  const setPrimaryStakeValue = useCallback(
    (value: string) => {
      if (singleBet) {
        handleStakeChange(
          singleBet.outcomeId,
          value,
          updateSinglesStakeAmount,
          true,
          singleBet.isLive ? 'inPlay' : 'preMatch',
          1
        );
        return;
      }

      if (!primaryStakeKey) {
        return;
      }

      handleStakeChange(
        primaryStakeKey,
        value,
        updateStakeAmount,
        false,
        betslipTicketType,
        Number(lastCombinationValue) || 1
      );
    },
    [
      betslipTicketType,
      handleStakeChange,
      lastCombinationValue,
      primaryStakeKey,
      singleBet,
      updateSinglesStakeAmount,
      updateStakeAmount,
    ]
  );

  const applyPresetAmount = useCallback(
    (amount: number) => {
      setPrimaryStakeValue(amount.toString());
    },
    [setPrimaryStakeValue]
  );

  const isPresetActive = useCallback(
    (amount: number) => primaryStakeValue !== '' && Number(primaryStakeValue) === amount,
    [primaryStakeValue]
  );

  const primaryStakeError = primaryStakeKey ? stakeErrors[primaryStakeKey] : undefined;
  const showUpdateStakesHelper = hasUpdateStakesAction(stakeErrors);
  const updateStakesHelperMessageKey = showUpdateStakesHelper ? 'updateStakesAdjustToLimits' : undefined;

  return {
    open,
    setOpen,
    showSystemBetInputs,
    toggleSystemBetInputs,
    selectedMenuItem,
    selectedPlayer,
    setSelectedPlayer,
    closeBetslipDrawer,
    discardBetslip,
    localizedBankerMenuOptions,
    localizedWalletTypeOptions,
    stakeErrors,
    removeSingleStakeError,
    errors,
    infoMessages,
    bets,
    removeBet,
    selectedWallet,
    betslipTicketType,
    isWaysTicket,
    hasBetBuilderSelection,
    isAgent,
    toggleBanker,
    isMaxBankerCountReached,
    hasPendingBetslips,
    placeBetMutation,
    betTypes,
    preparedBets,
    maxPayout,
    bonusAmount,
    totalWinningAmount,
    totalStakeAmount,
    totalOdds,
    minWinningAmount,
    minOdds,
    maxOdds,
    isSystemTicket,
    isAuthenticated,
    showInsufficientFunds,
    isPlaceBetButtonDisabled,
    hasReofferedBetslips,
    drawerOpen,
    combinations,
    lastCombinationKey,
    lastCombinationValue,
    handleMenuChange,
    handleWalletChange,
    placeBet,
    updateStakes,
    disabledSystems,
    primaryStakeMode,
    primaryStakeKey,
    primaryStakeValue,
    primaryStakeError,
    setPrimaryStakeValue,
    applyPresetAmount,
    isPresetActive,
    showUpdateStakesHelper,
    updateStakesHelperMessageKey,
    presetAmounts: NEOBET_BETSLIP_PRESET_AMOUNTS,
    setStakeAmounts,
    stakeAmounts,
    updateStakeAmount,
    updateSinglesStakeAmount,
    validateStake,
    handleStakeChange,
  };
};

export type NeobetBetslipDrawerController = ReturnType<typeof useNeobetBetslipDrawer>;
